import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Chip,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDebouncedValue } from '@mantine/hooks';
import { IconArrowLeft, IconArrowUp, IconExternalLink, IconPhoto, IconSparkles } from '@tabler/icons-react';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import '@mantine/tiptap/styles.css';
import useApi from '../hooks/useApi';
import {
  fetchArticle,
  fetchArticlePreview,
  patchCowriteCanvas,
  streamCowrite,
  streamRewriteSelection,
  updateArticle,
  uploadArticleImage,
} from '../services/api';

const INITIAL_ASSISTANT_TEXT = "What's the article about? Drop a session note, a rough idea, or a working title.";

function createLocalId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function renderSaveBadge(state) {
  if (state === 'saving') return <Text size="sm" c="dimmed">saving...</Text>;
  if (state === 'failed') return <Text size="sm" c="red">save failed</Text>;
  return <Badge color="sage" variant="light">draft · autosaved</Badge>;
}

export default function AviaryV2ArticleCowritePage() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const back = location.state?.back || { path: '/v2/articles', label: 'Articles' };

  const [loading, setLoading] = useState(true);
  const [article, setArticle] = useState(null);
  const [thread, setThread] = useState([]);
  const [composer, setComposer] = useState('');
  const [canvasHtml, setCanvasHtml] = useState('');
  const [saveState, setSaveState] = useState('idle');
  const [sending, setSending] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [selectionActive, setSelectionActive] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const threadViewportRef = useRef(null);
  const isApplyingRemoteContentRef = useRef(false);
  const lastSavedCanvasRef = useRef('');
  const rewriteRangeRef = useRef(null);
  const imageInputRef = useRef(null);
  const [debouncedCanvasHtml] = useDebouncedValue(canvasHtml, 500);

  async function insertImageAtCursor(file) {
    if (!editor) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      notifications.show({ color: 'red', title: 'Unsupported image', message: 'JPEG, PNG, WebP, or GIF only.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      notifications.show({ color: 'red', title: 'Image too large', message: 'Max 5MB.' });
      return;
    }
    const tempUrl = URL.createObjectURL(file);
    editor.chain().focus().setImage({ src: tempUrl, alt: file.name.replace(/\.[^.]+$/, '') }).run();
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { url } = await uploadArticleImage(api, slug, fd);
      const { doc, tr, schema } = editor.state;
      let changed = false;
      doc.descendants((node, pos) => {
        if (node.type === schema.nodes.image && node.attrs.src === tempUrl) {
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, src: url });
          changed = true;
        }
      });
      if (changed) editor.view.dispatch(tr);
      URL.revokeObjectURL(tempUrl);
    } catch (err) {
      const { doc, tr, schema } = editor.state;
      const deletions = [];
      doc.descendants((node, pos) => {
        if (node.type === schema.nodes.image && node.attrs.src === tempUrl) {
          deletions.push({ from: pos, to: pos + node.nodeSize });
        }
      });
      deletions.reverse().forEach(({ from, to }) => tr.delete(from, to));
      if (deletions.length) editor.view.dispatch(tr);
      URL.revokeObjectURL(tempUrl);
      notifications.show({
        color: 'red',
        title: 'Image upload failed',
        message: err.response?.data?.error || err.message,
      });
    }
  }

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'article-image',
          loading: 'lazy',
        },
      }),
    ],
    content: '',
    editorProps: {
      handleDrop: (_view, event, _slice, moved) => {
        if (moved) return false;
        const files = Array.from(event.dataTransfer?.files || []).filter((file) => file.type.startsWith('image/'));
        if (files.length === 0) return false;
        event.preventDefault();
        files.forEach((file) => { insertImageAtCursor(file); });
        return true;
      },
      handlePaste: (_view, event) => {
        const files = Array.from(event.clipboardData?.files || []).filter((file) => file.type.startsWith('image/'));
        if (files.length === 0) return false;
        event.preventDefault();
        files.forEach((file) => { insertImageAtCursor(file); });
        return true;
      },
    },
    onSelectionUpdate: ({ editor: tiptap }) => {
      const { from, to } = tiptap.state.selection;
      setSelectionActive(to > from);
    },
    onUpdate: ({ editor: tiptap }) => {
      if (isApplyingRemoteContentRef.current) return;
      setCanvasHtml(tiptap.getHTML());
    },
  });

  // Push HTML into the editor without triggering user-typing paths.
  // Used for initial load + streaming updates (delta / complete).
  function applyRemoteCanvas(html) {
    if (!editor) return;
    isApplyingRemoteContentRef.current = true;
    editor.commands.setContent(html || '', false);
    setTimeout(() => { isApplyingRemoteContentRef.current = false; }, 0);
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await fetchArticle(api, slug);
        if (cancelled) return;
        const existingThread = Array.isArray(data?.cowriteThread) ? data.cowriteThread : [];
        const nextThread = existingThread.length > 0
          ? existingThread
          : [{ id: createLocalId('seed'), role: 'assistant', content: INITIAL_ASSISTANT_TEXT, ts: new Date().toISOString() }];
        const nextCanvas = String(data?.bodyHtml || '');
        setArticle(data);
        setThread(nextThread);
        setCanvasHtml(nextCanvas);
        lastSavedCanvasRef.current = nextCanvas;
        applyRemoteCanvas(nextCanvas);
      } catch (err) {
        notifications.show({
          title: "Couldn't load co-write draft",
          message: err.response?.data?.error || err.message,
          color: 'red',
        });
        navigate('/v2/articles');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [api, slug, navigate]);

  useEffect(() => {
    if (!threadViewportRef.current) return;
    threadViewportRef.current.scrollTo({
      top: threadViewportRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [thread]);

  useEffect(() => {
    if (!article || loading || sending || rewriting) return;
    const changed = debouncedCanvasHtml !== lastSavedCanvasRef.current;
    if (!changed) return;
    let cancelled = false;
    setSaveState('saving');
    patchCowriteCanvas(api, slug, debouncedCanvasHtml)
      .then((result) => {
        if (cancelled) return;
        const savedHtml = String(result?.canvasHtml || debouncedCanvasHtml);
        lastSavedCanvasRef.current = savedHtml;
        setSaveState('idle');
        setThread(Array.isArray(result?.thread) && result.thread.length > 0 ? result.thread : thread);
      })
      .catch(() => {
        if (!cancelled) setSaveState('failed');
      });
    return () => { cancelled = true; };
  }, [api, article, debouncedCanvasHtml, loading, rewriting, sending, slug, thread]);

  const latestAssistantQuickReplies = useMemo(() => {
    for (let i = thread.length - 1; i >= 0; i -= 1) {
      const turn = thread[i];
      if (turn?.role === 'assistant' && Array.isArray(turn.quickReplies) && turn.quickReplies.length > 0) {
        return turn.quickReplies.slice(0, 4);
      }
    }
    return [];
  }, [thread]);

  async function sendMessage(rawMessage) {
    const message = String(rawMessage || '').trim();
    if (!message || sending) return;
    const userTurn = { id: createLocalId('user'), role: 'user', content: message, ts: new Date().toISOString() };
    const placeholderId = createLocalId('assistant-stream');
    setSending(true);
    setComposer('');
    let streamCanvas = '';
    setThread((prev) => [
      ...prev,
      userTurn,
      { id: placeholderId, role: 'assistant', content: 'Writing...', ts: new Date().toISOString() },
    ]);
    try {
      await streamCowrite(
        api,
        slug,
        { userMessage: message, canvasHtml },
        {
          onDelta: (text) => {
            streamCanvas += String(text || '');
            setThread((prev) => prev.map((turn) => (
              turn.id === placeholderId
                ? { ...turn, content: `${turn.content}${String(text || '')}` }
                : turn
            )));
            setCanvasHtml(streamCanvas);
            applyRemoteCanvas(streamCanvas);
          },
          onComplete: (payload) => {
            const nextThread = Array.isArray(payload?.thread) ? payload.thread : [];
            const nextCanvas = String(payload?.canvasHtml || streamCanvas || canvasHtml);
            lastSavedCanvasRef.current = nextCanvas;
            setCanvasHtml(nextCanvas);
            applyRemoteCanvas(nextCanvas);
            if (nextThread.length > 0) setThread(nextThread);
            setSaveState('idle');
          },
          onError: (err) => {
            throw err;
          },
        }
      );
    } catch (err) {
      notifications.show({
        title: 'Could not stream update',
        message: err.message || 'Please try again.',
        color: 'red',
      });
      setThread((prev) => prev.filter((turn) => turn.id !== placeholderId));
    } finally {
      setSending(false);
    }
  }

  async function rewriteSelection() {
    if (!editor || rewriting || !selectionActive) return;
    const { from, to } = editor.state.selection;
    if (to <= from) return;
    const plainText = editor.state.doc.textBetween(0, editor.state.doc.content.size, ' ');
    const selectedText = editor.state.doc.textBetween(from, to, ' ');
    const contextBefore = plainText.slice(Math.max(0, from - 200), from);
    const contextAfter = plainText.slice(to, to + 200);
    const instruction = window.prompt('Rewrite instruction (optional):', 'Make it more concise') || '';
    rewriteRangeRef.current = { from, to };
    setRewriting(true);
    let replacement = '';
    try {
      await streamRewriteSelection(
        api,
        slug,
        { selectedText, contextBefore, contextAfter, instruction },
        {
          onDelta: (chunk) => {
            replacement += String(chunk || '');
            const range = rewriteRangeRef.current;
            if (!range) return;
            editor.commands.insertContentAt(
              { from: range.from, to: range.to },
              replacement,
              { updateSelection: true }
            );
            rewriteRangeRef.current = { from: range.from, to: range.from + replacement.length };
            setCanvasHtml(editor.getHTML());
          },
          onComplete: () => {},
          onError: (err) => {
            throw err;
          },
        }
      );
      notifications.show({
        title: 'Selection rewritten',
        message: 'Review and keep editing from chat.',
        color: 'sage',
      });
    } catch (err) {
      notifications.show({
        title: 'Rewrite failed',
        message: err.message || 'Please try again.',
        color: 'red',
      });
    } finally {
      rewriteRangeRef.current = null;
      setRewriting(false);
    }
  }

  async function publishAndPromote() {
    if (publishing) return;
    setPublishing(true);
    try {
      await updateArticle(api, slug, { articleStatus: 'published' });
      navigate(`/v2/articles/${slug}/promote`, {
        state: { back: { path: `/v2/articles/${slug}/cowrite`, label: 'Co-write' } },
      });
    } catch (err) {
      notifications.show({
        title: 'Could not publish',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
      setPublishing(false);
    }
  }

  async function openPreview() {
    try {
      // Force a sync of the current canvas before rendering so preview is fresh.
      if (canvasHtml && canvasHtml !== lastSavedCanvasRef.current) {
        await patchCowriteCanvas(api, slug, canvasHtml);
        lastSavedCanvasRef.current = canvasHtml;
      }
      const html = await fetchArticlePreview(api, slug);
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke shortly after so it's usable but not leaked long-term.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      notifications.show({
        title: 'Could not open preview',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    }
  }

  if (loading || !article) {
    return (
      <Box p="xl" style={{ minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader color="sage" />
      </Box>
    );
  }

  return (
    <Box
      style={{
        padding: '16px 0',
        width: '90%',
        margin: '0 auto',
        height: 'calc(100vh - 32px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <Group justify="space-between" align="center">
        <style>{`.article-image { max-width: 100%; height: auto; border-radius: 8px; }`}</style>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          style={{ display: 'none' }}
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            event.currentTarget.value = '';
            if (file) insertImageAtCursor(file);
          }}
        />
        <Text size="sm" c="dimmed">Articles / Add / Co-write</Text>
        <Group gap="md" align="center">
          <UnstyledButton
            onClick={openPreview}
            style={{
              color: 'var(--mantine-color-sage-7)',
              fontSize: 13,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <IconExternalLink size={14} /> Preview
          </UnstyledButton>
          {renderSaveBadge(saveState)}
        </Group>
      </Group>

      <Box
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: '30fr 70fr',
          gap: 16,
        }}
      >
        {/* CHAT PANE */}
        <Paper
          withBorder
          radius="md"
          p="md"
          style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
        >
          <Group justify="space-between" align="center" mb="xs">
            <Text fw={600}>Aviary writer</Text>
            <Text size="sm" c="dimmed">your voice</Text>
          </Group>

          <ScrollArea
            viewportRef={threadViewportRef}
            offsetScrollbars
            style={{ flex: 1, minHeight: 0 }}
          >
            <Stack gap="sm" pr="xs">
              {thread.map((turn) => {
                const isUser = turn.role === 'user';
                return (
                  <Box
                    key={turn.id}
                    style={{
                      alignSelf: isUser ? 'flex-end' : 'flex-start',
                      maxWidth: '90%',
                      background: isUser ? 'var(--mantine-color-sage-6)' : 'var(--aviary-surface)',
                      color: isUser ? '#fff' : 'var(--aviary-ink)',
                      borderRadius: 14,
                      padding: isUser ? '11px 16px' : '10px 12px',
                      boxShadow: isUser ? '0 2px 6px rgba(47, 122, 104, 0.18)' : 'none',
                    }}
                  >
                    <Text
                      size="sm"
                      fw={isUser ? 500 : 400}
                      style={{ whiteSpace: 'pre-wrap', color: 'inherit', lineHeight: 1.5 }}
                    >
                      {turn.content}
                    </Text>
                  </Box>
                );
              })}
            </Stack>
          </ScrollArea>

          {latestAssistantQuickReplies.length > 0 && (
            <Group gap={6} mt="sm">
              {latestAssistantQuickReplies.map((chip) => (
                <Chip
                  key={chip}
                  checked={false}
                  onChange={() => sendMessage(chip)}
                  variant="light"
                  size="xs"
                  color="sage"
                  disabled={sending}
                >
                  {chip}
                </Chip>
              ))}
            </Group>
          )}

          <Group mt="sm" align="flex-end" wrap="nowrap">
            <Textarea
              placeholder="Ask for a change, or paste more notes..."
              value={composer}
              onChange={(e) => setComposer(e.currentTarget.value)}
              minRows={2}
              maxRows={4}
              style={{ flex: 1 }}
              disabled={sending}
            />
            <ActionIcon
              color="sage"
              variant="filled"
              size="lg"
              onClick={() => sendMessage(composer)}
              disabled={sending || composer.trim().length === 0}
            >
              <IconArrowUp size={18} />
            </ActionIcon>
          </Group>
        </Paper>

        {/* EDITOR PANE */}
        <Paper
          withBorder
          radius="md"
          p="md"
          style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
        >
          <RichTextEditor
            editor={editor}
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
          >
            <RichTextEditor.Toolbar>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold />
                <RichTextEditor.Italic />
                <RichTextEditor.H2 />
                <RichTextEditor.Blockquote />
              </RichTextEditor.ControlsGroup>
              <RichTextEditor.ControlsGroup>
                <Button
                  size="xs"
                  variant="subtle"
                  leftSection={<IconSparkles size={14} />}
                  onClick={rewriteSelection}
                  disabled={!selectionActive || rewriting || sending}
                >
                  Rewrite selection
                </Button>
                <Button
                  size="xs"
                  variant="subtle"
                  leftSection={<IconPhoto size={14} />}
                  onClick={() => imageInputRef.current?.click()}
                  disabled={sending || rewriting}
                >
                  Insert image
                </Button>
              </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>
            <RichTextEditor.Content
              style={{ flex: 1, minHeight: 0, overflow: 'auto' }}
            />
          </RichTextEditor>

          <Group gap={8} mt="sm">
            <Chip checked={false} onChange={() => sendMessage('Continue the article from where it left off.')} disabled={sending}>
              + Continue writing
            </Chip>
            <Chip checked={false} onChange={() => sendMessage('Add a new section that builds on the last one.')} disabled={sending}>
              + Add section
            </Chip>
          </Group>
        </Paper>
      </Box>

      <Group justify="space-between" align="center">
        <UnstyledButton onClick={() => navigate(back.path)} style={{ color: 'var(--aviary-muted)', fontSize: 14 }}>
          <IconArrowLeft size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Back
        </UnstyledButton>
        <Button color="sage" onClick={publishAndPromote} loading={publishing}>
          Publish
        </Button>
      </Group>
    </Box>
  );
}
